import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { processBatch, processSingleProduct, reprocessProduct, swapTitle, switchPlatform, cleanRawTitle, cleanRawDescription, cleanRawSku } from '../lib/slPipeline';
import { loadSettings as loadSmartLayerSettings } from '../lib/smartlayerBridge';
import { warmUpWorker } from '../utils/smartLayerWorker';
import * as storage from '../utils/amorliaStorage';

const AmorliaDashboardContext = createContext(null);

// Extract the base title by removing color/size/variant suffixes for grouping
function getBaseTitle(title) {
  return title
    .toLowerCase()
    .replace(/\b(gold|silver|rose gold|black|white|blue|green|red|pink|purple|brown)\b/gi, '')
    .replace(/\b(small|medium|large|xl|xxl|\d+(?:\.\d+)?\s*(?:in|cm|mm|inch))\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 50);
}

// Group products that appear to be variants of the same base product
function groupVariants(rawProducts) {
  const grouped = [];
  const seen = new Map();

  for (const raw of rawProducts) {
    const baseTitle = getBaseTitle(raw.title || '');
    const sourceBase = (raw.sourceUrl || raw._url || '').split('?')[0].split('#')[0];
    const key = baseTitle + '|||' + sourceBase;

    if (seen.has(key)) {
      const existing = seen.get(key);
      existing.variants = existing.variants || [];
      existing.variants.push({
        title: raw.title,
        price: raw.price || 0,
        sku: raw.sku || '',
        images: raw.images || [],
        sourceUrl: raw.sourceUrl || raw._url || '',
        _raw: raw,
      });
    } else {
      const product = { ...raw, variants: raw.variants || [] };
      seen.set(key, product);
      grouped.push(product);
    }
  }

  return grouped;
}

// Check if a product already has SmartLayer data
function isAlreadySmartLayerProcessed(product) {
  if (!product || typeof product !== 'object') return false;
  return !!(
    product.slStatus ||
    product.slOk != null ||
    product.score != null ||
    product.pricing != null ||
    product.publishGate != null ||
    (product.titleVariants && product.titleVariants.length > 0) ||
    product.orderEntry != null ||
    product.smartLayerProcessed
  );
}

export function AmorliaDashboardProvider({ children }) {
  // ── Core shared state (persisted) ──
  const [extractQueue, setExtractQueue] = useState(
    () => storage.load(storage.KEYS.extractQueue, [])
  );
  const [slData, setSlData] = useState(
    () => storage.load(storage.KEYS.slData, {})
  );
  const [activeJobs, setActiveJobs] = useState(
    () => storage.load(storage.KEYS.activeJobs, [])
  );
  const [pageDraftStates, setPageDraftStates] = useState(
    () => storage.load(storage.KEYS.pageDraftStates, {})
  );
  const [lastActivePage, setLastActivePage] = useState(
    () => storage.load(storage.KEYS.lastActivePage, 'extract')
  );

  // ── Non-persisted transient state ──
  const [isImporting, setIsImporting] = useState(false);
  const [importSourceInfo, setImportSourceInfo] = useState('');
  const [extensionStatus, setExtensionStatus] = useState('unknown');

  // ── Refs ──
  const broadcastRef = useRef(null);
  const isMountedRef = useRef(false);

  // ── BroadcastChannel for cross-tab sync ──
  useEffect(() => {
    try {
      broadcastRef.current = new BroadcastChannel('amorlia_dashboard_sync');
      broadcastRef.current.onmessage = (e) => {
        const { type, data } = e.data || {};
        if (type === 'queue_updated') {
          setExtractQueue(data);
        } else if (type === 'sl_data_updated') {
          setSlData(data);
        } else if (type === 'jobs_updated') {
          setActiveJobs(data);
        } else if (type === 'products_received') {
          // Another tab received products — refresh our queue from storage
          const stored = storage.load(storage.KEYS.extractQueue, []);
          setExtractQueue(stored);
        }
      };
    } catch {
      // BroadcastChannel not supported
    }
    return () => {
      if (broadcastRef.current) {
        broadcastRef.current.close();
        broadcastRef.current = null;
      }
    };
  }, []);

  const broadcast = useCallback((type, data) => {
    if (broadcastRef.current) {
      try { broadcastRef.current.postMessage({ type, data }); } catch {}
    }
  }, []);

  // ── Auto-save: persist state changes to localStorage ──
  useEffect(() => {
    storage.debouncedSave(storage.KEYS.extractQueue, extractQueue, 300);
    broadcast('queue_updated', extractQueue);
  }, [extractQueue, broadcast]);

  useEffect(() => {
    storage.debouncedSave(storage.KEYS.slData, slData, 500);
    broadcast('sl_data_updated', slData);
  }, [slData, broadcast]);

  useEffect(() => {
    storage.debouncedSave(storage.KEYS.activeJobs, activeJobs, 500);
  }, [activeJobs]);

  useEffect(() => {
    storage.debouncedSave(storage.KEYS.pageDraftStates, pageDraftStates, 500);
  }, [pageDraftStates]);

  useEffect(() => {
    storage.save(storage.KEYS.lastActivePage, lastActivePage);
  }, [lastActivePage]);

  // ── Transform raw product into dashboard format (Stage 1: basic display) ──
  const transformRaw = useCallback((raw) => {
    const guessMetalFromTitle = (title) => {
      if (!title) return 'Stainless Steel';
      const l = title.toLowerCase();
      if (l.includes('rose gold')) return 'Rose Gold Tone';
      if (l.includes('gold')) return 'Gold Tone';
      if (l.includes('silver')) return 'Silver Tone';
      if (l.includes('stainless')) return 'Stainless Steel';
      if (l.includes('copper')) return 'Copper';
      return 'Stainless Steel';
    };
    const toTitleCase = (str) => str.replace(/\w\S*/g, t => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

    const sourcePrice = raw.price || raw.rawNihaoPrice || 0;
    const suggestPrice = sourcePrice > 0 ? Math.round(sourcePrice * 3 * 100) / 100 : 0;
    const img = raw.images && raw.images.length > 0 ? raw.images[0] : '';
    // Use pipeline cleaners for consistent title/description/sku
    const eBayTitle = toTitleCase(cleanRawTitle(raw.title || '')).slice(0, 80);
    const resolvedSourceUrl = raw._url || raw.sourceUrl || '';
    const resolvedSku = cleanRawSku(raw.sku || raw.supplierSku || '');
    const resolvedSite = raw._source || raw.source || raw.sourceSite || 'nihao';
    const cleanDesc = cleanRawDescription(raw.description || '');

    return {
      id: 'ext_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      sourceUrl: resolvedSourceUrl,
      sourceSite: resolvedSite,
      scrapedAt: raw._scrapedAt || new Date().toISOString(),
      variantName: (raw.category || 'Jewelry') + ' — ' + (resolvedSku || 'Imported'),
      title: eBayTitle || 'Imported Product',
      altTitle: '',
      description: cleanDesc,
      price: suggestPrice > 0 ? suggestPrice : null,
      rawNihaoPrice: sourcePrice,
      sourcePrice: sourcePrice,
      image: img,
      images: raw.images || [],
      sku: resolvedSku,
      category: raw.category || 'Jewelry',
      material: raw.material || '',
      chainLength: raw.chainLength || '',
      stock: true,
      itemSpecifics: {
        Brand: 'Unbranded',
        Material: raw.material || 'Stainless Steel',
        Metal: guessMetalFromTitle(raw.title),
        Style: 'Pendant',
        Occasion: 'Casual, Gift',
        Type: raw.category || 'Necklace',
        Length: raw.chainLength || '18 in',
        'Country/Region of Manufacture': 'China',
        Returns: '30 Day Returns',
        ...(raw.itemSpecifics || {})
      },
      status: 'pending',
      priceConfirmed: sourcePrice > 0,
      hasImages: !!(raw.images && raw.images.length > 0),
      voice: 'factual',
      variants: raw.variants || [],
      rawData: raw,
      processingStatus: 'pending'
    };
  }, []);

  // ── Universal product handler (global, not page-bound) ──
  const handleIncomingProducts = useCallback(async (rawProducts, source) => {
    if (!Array.isArray(rawProducts) || rawProducts.length === 0) return;

    console.log('[Amorlia Dashboard] incoming products:', rawProducts.length, source);
    setIsImporting(true);

    // Group variants before processing
    const grouped = groupVariants(rawProducts);

    const alreadyProcessed = [];
    const needsProcessing = [];

    for (const raw of grouped) {
      if (isAlreadySmartLayerProcessed(raw)) {
        alreadyProcessed.push({ ...raw, processingStatus: 'done' });
      } else {
        needsProcessing.push(raw);
      }
    }

    // Add already-processed products immediately
    if (alreadyProcessed.length > 0) {
      setExtractQueue(prev => {
        const existingUrls = new Set(prev.map(v => (v.sourceUrl || '').toLowerCase()).filter(Boolean));
        const existingIds = new Set(prev.map(v => v.id));
        const newOnes = alreadyProcessed.filter(p => {
          const url = (p.sourceUrl || p._url || '').toLowerCase();
          const id = p.id;
          return (!url || !existingUrls.has(url)) && !existingIds.has(id);
        });
        return [...prev, ...newOnes];
      });
    }

    // Transform raw products, add as pending, then process sequentially
    if (needsProcessing.length > 0) {
      const transformed = needsProcessing.map(raw => {
        const t = transformRaw(raw);
        return { ...t, id: t.id || 'prod_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6) };
      });

      // Deduplicate and add immediately (Stage 1 display)
      setExtractQueue(prev => {
        const existingUrls = new Set(prev.map(v => (v.sourceUrl || '').toLowerCase()).filter(Boolean));
        const existingIds = new Set(prev.map(v => v.id));
        const unique = transformed.filter(t => {
          const url = (t.sourceUrl || '').toLowerCase();
          return (!url || !existingUrls.has(url)) && !existingIds.has(t.id);
        });
        return [...prev, ...unique];
      });

      // SmartLayer sequential processing with progressive UI updates
      try {
        let readyCount = 0;
        const processed = await processBatch(transformed, (doneProduct) => {
          // Update this specific product in the queue as soon as it's done
          setExtractQueue(prev => prev.map(v =>
            v.id === doneProduct.id ? { ...doneProduct, processingStatus: 'done' } : v
          ));
          if (doneProduct._sl) {
            setSlData(prev => ({ ...prev, [doneProduct.id]: doneProduct._sl }));
          }
          if (doneProduct.slStatus === 'READY_TO_REVIEW') readyCount++;
        });

        setImportSourceInfo(`${processed.length} product(s) imported, SmartLayer optimized (${readyCount} ready).`);
      } catch (e) {
        console.error('[Amorlia Dashboard] SmartLayer processing failed:', e);
        setImportSourceInfo(`${needsProcessing.length} product(s) imported (SmartLayer processing pending).`);
      }
    } else if (alreadyProcessed.length > 0) {
      setImportSourceInfo(`${alreadyProcessed.length} product(s) imported (already processed).`);
    }

    setIsImporting(false);
    broadcast('products_received', null);
  }, [transformRaw, broadcast]);

  // ── Extension listeners (global, mounted once) ──
  useEffect(() => {
    if (isMountedRef.current) return; // prevent double-mount in StrictMode
    isMountedRef.current = true;

    // Warm up SmartLayer worker at app level
    warmUpWorker();

    // CHANNEL 1: CustomEvent
    const onCustomEvent = (e) => {
      handleIncomingProducts(e.detail?.products || [], 'custom-event');
    };
    window.addEventListener('amorlia_products_ready', onCustomEvent);

    // CHANNEL 2: postMessage
    const onMessage = (e) => {
      const type = e.data?.type;
      if (!type) return;
      if (
        type === 'AMORLIA_EXTENSION_PRODUCTS_READY' ||
        type === 'SMARTLAYER_PRODUCTS_READY' ||
        type === 'AMORLIA_PRODUCTS'
      ) {
        handleIncomingProducts(e.data.products || [], 'postMessage');
      }
    };
    window.addEventListener('message', onMessage);

    // CHANNEL 3: localStorage polling (every 2s)
    const storagePollInterval = setInterval(() => {
      try {
        const stored = localStorage.getItem(storage.KEYS.pendingProducts);
        if (stored) {
          const products = JSON.parse(stored);
          if (Array.isArray(products) && products.length > 0) {
            localStorage.removeItem(storage.KEYS.pendingProducts);
            handleIncomingProducts(products, 'localStorage-poll');
          }
        }
      } catch {}
    }, 2000);

    // CHANNEL 4: chrome.storage.local polling (every 3s)
    const extStoragePollInterval = setInterval(() => {
      try {
        if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
          chrome.storage.local.get(storage.KEYS.pendingProducts, (data) => {
            const products = data[storage.KEYS.pendingProducts];
            if (Array.isArray(products) && products.length > 0) {
              chrome.storage.local.set({ [storage.KEYS.pendingProducts]: [] });
              handleIncomingProducts(products, 'extension-storage');
              setExtensionStatus('connected');
            }
          });
          setExtensionStatus('connected');
        }
      } catch {}
    }, 3000);

    // Initial seed from localStorage
    try {
      const stored = localStorage.getItem(storage.KEYS.pendingProducts);
      if (stored) {
        const products = JSON.parse(stored);
        if (Array.isArray(products) && products.length > 0) {
          localStorage.removeItem(storage.KEYS.pendingProducts);
          handleIncomingProducts(products, 'localStorage-init');
        }
      }
    } catch {}

    // Rehydrate: mark interrupted jobs
    setActiveJobs(prev => prev.map(j =>
      j.status === 'running' ? { ...j, status: 'interrupted' } : j
    ));

    return () => {
      window.removeEventListener('amorlia_products_ready', onCustomEvent);
      window.removeEventListener('message', onMessage);
      clearInterval(storagePollInterval);
      clearInterval(extStoragePollInterval);
    };
  }, [handleIncomingProducts]);

  // ── Queue operations (used by ExtractPage) ──
  const updateVariant = useCallback((id, field, value) => {
    setExtractQueue(prev => prev.map(v => {
      if (v.id !== id) return v;
      if (field.startsWith('itemSpecifics.')) {
        const key = field.replace('itemSpecifics.', '');
        return { ...v, itemSpecifics: { ...v.itemSpecifics, [key]: value } };
      }
      return { ...v, [field]: value };
    }));
  }, []);

  const removeVariant = useCallback((id) => {
    if (id == null) return;
    setExtractQueue(prev => prev.filter(v => v.id !== id));
    setSlData(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const clearExtractQueue = useCallback(() => {
    setExtractQueue([]);
    setSlData({});
    storage.save(storage.KEYS.extractQueue, []);
    storage.save(storage.KEYS.slData, {});
  }, []);

  const setExtractQueueDirect = useCallback((updater) => {
    setExtractQueue(updater);
  }, []);

  const setSlDataDirect = useCallback((updater) => {
    setSlData(updater);
  }, []);

  // ── Job management ──
  const addJob = useCallback((job) => {
    const newJob = {
      id: 'job_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      progress: 0,
      ...job,
      status: job.status || 'queued',
    };
    setActiveJobs(prev => [...prev, newJob]);
    return newJob.id;
  }, []);

  const updateJob = useCallback((jobId, updates) => {
    setActiveJobs(prev => prev.map(j =>
      j.id === jobId ? { ...j, ...updates, updatedAt: new Date().toISOString() } : j
    ));
  }, []);

  const removeJob = useCallback((jobId) => {
    setActiveJobs(prev => prev.filter(j => j.id !== jobId));
  }, []);

  // ── Page draft state ──
  const savePageDraft = useCallback((pageId, key, value) => {
    setPageDraftStates(prev => ({
      ...prev,
      [pageId]: { ...(prev[pageId] || {}), [key]: value },
    }));
  }, []);

  const getPageDraft = useCallback((pageId, key, fallback = null) => {
    const pageState = pageDraftStates[pageId];
    if (!pageState) return fallback;
    return pageState[key] !== undefined ? pageState[key] : fallback;
  }, [pageDraftStates]);

  // ── Context value ──
  const value = {
    // Core state
    extractQueue,
    slData,
    isImporting,
    importSourceInfo,
    activeJobs,
    pageDraftStates,
    lastActivePage,
    extensionStatus,

    // Queue operations
    setExtractQueue: setExtractQueueDirect,
    setSlData: setSlDataDirect,
    updateVariant,
    removeVariant,
    clearExtractQueue,
    setImportSourceInfo,
    setIsImporting,

    // Product import
    handleIncomingProducts,
    transformRaw,

    // Job management
    addJob,
    updateJob,
    removeJob,

    // Page state
    savePageDraft,
    getPageDraft,
    setLastActivePage,

    // Broadcast
    broadcast,
  };

  return (
    <AmorliaDashboardContext.Provider value={value}>
      {children}
    </AmorliaDashboardContext.Provider>
  );
}

export function useAmorliaDashboard() {
  const ctx = useContext(AmorliaDashboardContext);
  if (!ctx) throw new Error('useAmorliaDashboard must be used within AmorliaDashboardProvider');
  return ctx;
}

export default AmorliaDashboardContext;
